import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { getSessionStatus } from '@dealstack/frontend/shared/dataExchange/services/session.service';
import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { redirectTo, replaceWith } from '@dealstack/frontend/shared/redirecting';
import { ApplicationType } from '@dealstack/shared/lib/generated';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { safeReturnPath, storeReturnPath } from './returnPath';
import { SessionEndedNotice } from './SessionEndedNotice';
import { authStartUrl, involuntarySessionEndReason } from './startHostedAuth';

interface Props {
	applicationType: ApplicationType;
}

const DEFAULT_LANDING_PATH = '/dashboard';

export const AuthStart = ({ applicationType }: Props) => {
	const globalConfig = useGlobalConfig();
	const [searchParams] = useSearchParams();

	const returnPath = searchParams.get('redirectUrl');
	const sessionEndReason = involuntarySessionEndReason(searchParams);
	const [isCheckingSession, setIsCheckingSession] = useState(Boolean(sessionEndReason));

	const startHostedAuth = () => {
		storeReturnPath(returnPath);
		redirectTo(authStartUrl(globalConfig.apiUrl, applicationType));
	};

	// Replaced, not pushed: a notice left in history resurfaces on Back long after the
	// user has signed in again, still saying their session ended.
	const continueFromNotice = () => {
		storeReturnPath(returnPath);
		replaceWith(authStartUrl(globalConfig.apiUrl, applicationType));
	};

	useEffect(() => {
		if (!sessionEndReason) {
			startHostedAuth();
			return;
		}

		// The notice is only true if the session is gone. Back and the bfcache can bring it
		// back while a newer session is live, so ask the server before showing it.
		const leaveIfSignedIn = () => {
			setIsCheckingSession(true);
			getSessionStatus().then(
				() => replaceWith(safeReturnPath(returnPath) ?? DEFAULT_LANDING_PATH),
				() => setIsCheckingSession(false)
			);
		};

		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) {
				leaveIfSignedIn();
			}
		};

		leaveIfSignedIn();
		window.addEventListener('pageshow', onPageShow);

		return () => window.removeEventListener('pageshow', onPageShow);
	}, []);

	if (sessionEndReason && !isCheckingSession) {
		return <SessionEndedNotice reason={sessionEndReason} onContinue={continueFromNotice} />;
	}

	return <FullscreenLoader name="pageLoading" />;
};
